import _ from "lodash";
import React from "react";
import {
  StaffMemberOutlined,
  StudentOutlined,
  FamilyOutlined,
  ClassChannelOutlined,
  CustomChannelOutlined,
  FamilyChannelOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import classes from "./SettingsUtils.scss";
import {
  isMessagingCurriculumWiseSplitEnabled,
  isMessagingRnpEnabled,
  isMessagingV2ChannelEnabled,
  isPaidConnectModule,
  showAudioRecordingConfigSetting,
} from "MessagingV2/modules/Utils";
import { getIsModuleSettingsV2Enabled } from "Utils/ACLStoreUtils";

export const NAVIGATION_ROUTES = {
  ROOT: { value: "root" },
  GENERAL_SETTINGS: { value: "GENERAL_SETTINGS" },
  MODULE_SETTINGS: { value: "MODULE_SETTINGS", route: "module-settings" },
  VIEW_CHATS: { value: "VIEW_CHATS" },
  TEACHERS_CHATS: { value: "TEACHERS_CHATS", route: "chat-view/teachers-chat" },
  STUDENTS_CHATS: { value: "STUDENTS_CHATS", route: "chat-view/students-chat" },
  FAMILY_CHATS: {
    value: "FAMILY_CHATS",
    route: "chat-view/family-members-chat",
  },
  VIEW_CHANNELS: { value: "VIEW_CHANNELS" },
  CLASS_CHANNEL: {
    value: "CLASS_CHANNEL",
    route: "channel-view/class-channel",
  },
  CUSTOM_CHANNEL: {
    value: "CUSTOM_CHANNEL",
    route: "channel-view/custom-channel",
  },
  FAMILY_CHANNEL: {
    value: "FAMILY_CHANNEL",
    route: "channel-view/family-channel",
  },
};

export const getSettingRouteFromMessaging = ({ key }) =>
  `messaging/settings/${_.get(NAVIGATION_ROUTES, [key, "route"], "")}`;

export const getChatViewModeRoute = userId => `chat-view/${userId}/chats`;

export const getSideNavigationOptions = ({ t }) => [
  {
    key: NAVIGATION_ROUTES.ROOT.value,
    children: [
      NAVIGATION_ROUTES.GENERAL_SETTINGS.value,
      NAVIGATION_ROUTES.VIEW_CHATS.value,
      NAVIGATION_ROUTES.VIEW_CHANNELS.value,
    ],
  },
  {
    key: NAVIGATION_ROUTES.GENERAL_SETTINGS.value,
    title: t("common:general_settings"),
    children: [NAVIGATION_ROUTES.MODULE_SETTINGS.value],
    parent: NAVIGATION_ROUTES.ROOT.value,
    tier: 2,
  },
  {
    key: NAVIGATION_ROUTES.VIEW_CHATS.value,
    title: t("common:view_chats"),
    children: [
      NAVIGATION_ROUTES.TEACHERS_CHATS.value,
      NAVIGATION_ROUTES.STUDENTS_CHATS.value,
      NAVIGATION_ROUTES.FAMILY_CHATS.value,
    ],
    parent: NAVIGATION_ROUTES.ROOT.value,
    tier: 2,
  },
  {
    key: NAVIGATION_ROUTES.VIEW_CHANNELS.value,
    children: [
      NAVIGATION_ROUTES.CLASS_CHANNEL.value,
      NAVIGATION_ROUTES.CUSTOM_CHANNEL.value,
      NAVIGATION_ROUTES.FAMILY_CHANNEL.value,
    ],
    parent: NAVIGATION_ROUTES.ROOT.value,
    title: t("common:view_channels"),
    tier: 2,
  },
  {
    key: NAVIGATION_ROUTES.MODULE_SETTINGS.value,
    title: t("common:module_and_user_settings"),
    parent: NAVIGATION_ROUTES.GENERAL_SETTINGS.value,
    prefix: <SettingsOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.TEACHERS_CHATS.value,
    title: t("common:teacher_plural"),
    parent: NAVIGATION_ROUTES.VIEW_CHATS.value,
    prefix: <StaffMemberOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.STUDENTS_CHATS.value,
    title: t("common:student_other"),
    parent: NAVIGATION_ROUTES.VIEW_CHATS.value,
    prefix: <StudentOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.FAMILY_CHATS.value,
    title: t("common:family_member_other"),
    parent: NAVIGATION_ROUTES.VIEW_CHATS.value,
    prefix: <FamilyOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.CLASS_CHANNEL.value,
    title: t("common:class_channel_other"),
    parent: NAVIGATION_ROUTES.VIEW_CHANNELS.value,
    prefix: <ClassChannelOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.CUSTOM_CHANNEL.value,
    title: t("common:custom_channel_other"),
    parent: NAVIGATION_ROUTES.VIEW_CHANNELS.value,
    prefix: <CustomChannelOutlined variant={"subtle"} />,
    tier: 1,
  },
  {
    key: NAVIGATION_ROUTES.FAMILY_CHANNEL.value,
    title: t("common:family_channel_other"),
    parent: NAVIGATION_ROUTES.VIEW_CHANNELS.value,
    prefix: <FamilyChannelOutlined variant={"subtle"} />,
    tier: 1,
  },
];

export const CONFIGURATION = {
  MESSAGINGV2_FOR_STUDENT: "Setting:MessagingV2ForStudent",
  MESSAGINGV2_FOR_TEACHER: "Setting:MessagingV2ForTeacher",
  MESSAGINGV2_FOR_FAMILY: "Setting:MessagingV2ForFamily",
  AUDIO_RECORDING: "Settings:AllowAudioRecordingInConnect",
  CAN_STAFF_CREATE_AND_MANAGE_CHANNEL: "Setting:CanStaffCreateAndManageChannel",
  CAN_STAFF_CHANNEL_MEMBER_MANAGE_CHANNEL:
    "Setting:CanStaffChannelMemberManageChannel",
  CAN_STAFF_MODIFY_THEIR_MESSAGE: "Setting:CanStaffModifyTheirMessage",
  CAN_STUDENT_INITIATE_CHAT: "Setting:CanStudentInitiateChat",
  CAN_STUDENT_MODIFY_THEIR_MESSAGE: "Setting:CanStudentModifyTheirMessage",
  CAN_FAMILY_INITIATE_CHAT: "Setting:CanFamilyInitiateChat",
  CAN_FAMILY_MODIFY_THEIR_MESSAGE: "Setting:CanFamilyModifyTheirMessage",
};

export const IS_PAID_MODULE_CONFIG = "FeatureFlag:IsPaidConnectModule";

export const MESSAGING_GRADE_LEVEL_CONFIGS = [
  CONFIGURATION.MESSAGINGV2_FOR_STUDENT,
  CONFIGURATION.MESSAGINGV2_FOR_FAMILY,
  CONFIGURATION.CAN_STUDENT_INITIATE_CHAT,
  CONFIGURATION.CAN_STUDENT_MODIFY_THEIR_MESSAGE,
  CONFIGURATION.CAN_FAMILY_INITIATE_CHAT,
  CONFIGURATION.CAN_FAMILY_MODIFY_THEIR_MESSAGE,
];

export const MESSAGING_MODULE_GRADE_LEVEL_CONFIG = [
  CONFIGURATION.MESSAGINGV2_FOR_STUDENT,
  CONFIGURATION.MESSAGINGV2_FOR_FAMILY,
];

export const SUBMODULE_SETTINGS_V2 = [
  {
    key: "module_settings",
    label: "common:configuration_options",
    isHidden: () =>
      isMessagingRnpEnabled() &&
      getIsModuleSettingsV2Enabled() &&
      !showAudioRecordingConfigSetting(),
    children: [
      {
        key: "staff",
        label: "common:enable_messaging_module_for_teacher",
        configName: CONFIGURATION.MESSAGINGV2_FOR_TEACHER,
        configId: "4943",
        isHidden: isPaidCurriculumConnectModule =>
          isMessagingRnpEnabled() ||
          (isMessagingCurriculumWiseSplitEnabled()
            ? !isPaidCurriculumConnectModule
            : !isPaidConnectModule()),
      },
      {
        key: "student",
        label: "common:enable_messaging_module_for_student",
        configName: CONFIGURATION.MESSAGINGV2_FOR_STUDENT,
        configId: "4944",
        showGrade: true,
        isHidden: isPaidCurriculumConnectModule =>
          getIsModuleSettingsV2Enabled() ||
          (isMessagingCurriculumWiseSplitEnabled()
            ? !isPaidCurriculumConnectModule
            : !isPaidConnectModule()),
      },
      {
        key: "parent",
        label: "common:enable_messaging_module_for_family",
        configName: CONFIGURATION.MESSAGINGV2_FOR_FAMILY,
        configId: "4945",
        showGrade: true,
        isHidden: () => getIsModuleSettingsV2Enabled(),
      },
      {
        key: "audio_all",
        label: "common:allow_audio_recording",
        configName: CONFIGURATION.AUDIO_RECORDING,
        configId: "188410796676487372",
        showGrade: false,
        isHidden: () => !showAudioRecordingConfigSetting(),
      },
    ],
  },
  {
    key: "teacher_settings",
    label: "configuration:teacher_settings",
    isHidden: () => isMessagingRnpEnabled(),
    children: [
      {
        key: "create_and_manage_channel",
        label: "common:allow_teacher_to_create_and_manage_custom_channel",
        configName: CONFIGURATION.CAN_STAFF_CREATE_AND_MANAGE_CHANNEL,
        configId: "5013",
        isHidden: isPaidCurriculumConnectModule =>
          !isMessagingV2ChannelEnabled() ||
          (isMessagingCurriculumWiseSplitEnabled()
            ? !isPaidCurriculumConnectModule
            : !isPaidConnectModule()),
      },
      {
        key: "manage_channel",
        label: "common:allow_teacher_to_manage_channel_they_are_added",
        configName: CONFIGURATION.CAN_STAFF_CHANNEL_MEMBER_MANAGE_CHANNEL,
        configId: "5014",
        isHidden: isPaidCurriculumConnectModule =>
          !isMessagingV2ChannelEnabled() ||
          (isMessagingCurriculumWiseSplitEnabled()
            ? !isPaidCurriculumConnectModule
            : !isPaidConnectModule()),
      },
      {
        key: "modify_message",
        label: "configuration:allow_teacher_to_modify_their_messages",
        configName: CONFIGURATION.CAN_STAFF_MODIFY_THEIR_MESSAGE,
        configId: "5015",
      },
    ],
  },
  {
    key: "student_settings",
    label: "configuration:student_settings",
    isHidden: isPaidCurriculumConnectModule =>
      isMessagingCurriculumWiseSplitEnabled()
        ? !isPaidCurriculumConnectModule
        : !isPaidConnectModule(),
    children: [
      {
        key: "initiate_chat",
        label:
          "configuration:allow_student_to_initiate_conversations_with_teachers",
        configName: CONFIGURATION.CAN_STUDENT_INITIATE_CHAT,
        configId: "5016",
        showGrade: true,
      },
      {
        key: "modify_message",
        label: "configuration:allow_students_to_modify_their_messages",
        configName: CONFIGURATION.CAN_STUDENT_MODIFY_THEIR_MESSAGE,
        configId: "5017",
        showGrade: true,
      },
    ],
  },
  {
    key: "family_settings",
    label: "common:family_settings",
    children: [
      {
        key: "initiate_chat",
        label:
          "configuration:allow_family_members_to_initiate_conversations_with_teachers",
        configName: CONFIGURATION.CAN_FAMILY_INITIATE_CHAT,
        configId: "5018",
        showGrade: true,
      },
      {
        key: "modify_message",
        label: "configuration:allow_family_members_to_modify_their_messages",
        configName: CONFIGURATION.CAN_FAMILY_MODIFY_THEIR_MESSAGE,
        configId: "22817969625109422",
        showGrade: true,
      },
    ],
  },
];

export const getV2ModalConfigByConfigId = ({
  t,
  configId,
  isPaidCurriculumConnectModule,
}) => {
  let title = "";
  const sections = [];
  let width = "440px";

  const isPaidModule = isMessagingCurriculumWiseSplitEnabled()
    ? !isPaidCurriculumConnectModule
    : !isPaidConnectModule();

  switch (configId) {
    case "4944":
    case "4945":
      if (isPaidModule) {
        title = t("common:manage_module_for_family_members_at_grade_level");
        width = "543px";
      } else {
        title = t("common:manage_module_for_student_and_family_grade_level");
        width = "664px";
        sections.push(
          {
            title: t("configuration:enable_for_students"),
            configId: "4944",
          },
          {
            title: t("configuration:enable_for_family"),
            configId: "4945",
          }
        );
      }
      break;
    case "5016":
      title = t(
        "configuration:allow_student_to_initiate_conversations_with_teachers"
      );
      width = "543px";
      break;
    case "5017":
      title = t("configuration:allow_students_to_modify_their_messages");
      width = "543px";
      break;
    case "5018":
      title = t(
        "configuration:allow_family_members_to_initiate_conversations_with_teachers"
      );
      width = "543px";
      break;
    case "22817969625109422":
      title = t("configuration:allow_family_members_to_modify_their_messages");
      width = "543px";
      break;
  }

  return {
    title,
    sections,
    width,
  };
};

export const getMessagingSubModule = ({ t }) => {
  return {
    moduleSetting: {
      labelKey: "module_settings",
      customLabels: {
        studentLabel: t("configuration:enable_for_students"),
        parentLabel: t("configuration:enable_for_family_members"),
      },

      nodes: [
        {
          labelKey: "messaging",
          uiType: "BOOLEAN",
          overrideClasses: {
            parent: {
              container: classes.container,
            },
          },
          nodes: [
            {
              metadata: {
                depth: 3,
                conf_id: 4944,
                columnId: "student",
                configName: CONFIGURATION.MESSAGINGV2_FOR_STUDENT,
              },
              uiType: "BOOLEAN",
              viewType: "CONFIGURATION",
              displaySequence: 1,
              configuration: {
                id: "4944",
              },
            },
            {
              metadata: {
                depth: 3,
                conf_id: 4943,
                columnId: "staff",
                configName: CONFIGURATION.MESSAGINGV2_FOR_TEACHER,
              },
              uiType: "BOOLEAN",
              viewType: "CONFIGURATION",
              displaySequence: 2,
              configuration: {
                id: "4943",
              },
            },
            {
              metadata: {
                depth: 3,
                conf_id: 4945,
                columnId: "parent",
                configName: CONFIGURATION.MESSAGINGV2_FOR_FAMILY,
              },
              uiType: "BOOLEAN",
              viewType: "CONFIGURATION",
              displaySequence: 3,
              configuration: {
                id: "4945",
              },
            },
          ],
        },
      ],
    },
    teacherSetting: {
      labelKey: "teacher_settings",
      metadata: {
        depth: 2,
      },
      showGridLayout: false,
      nodes: [
        {
          labelKey: "allow_teachers_to_create_and_manage_their_channels",
          metadata: {
            configurationSubTitle:
              "allow_teachers_to_create_and_manage_their_channels_subtext",
            depth: 3,
            conf_id: 5013,
            configName: CONFIGURATION.CAN_STAFF_CREATE_AND_MANAGE_CHANNEL,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 1,
          configuration: {
            id: "5013",
          },
        },
        {
          labelKey:
            "allow_teacher_to_manage_and_control_channel_they_are_added",
          metadata: {
            configurationSubTitle:
              "allow_teacher_to_manage_and_control_channel_they_are_added_subtext",
            depth: 3,
            conf_id: 5014,
            configName: CONFIGURATION.CAN_STAFF_CHANNEL_MEMBER_MANAGE_CHANNEL,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 2,
          configuration: {
            id: "5014",
          },
        },
        {
          labelKey: "allow_teacher_to_modify_their_messages",
          metadata: {
            configurationSubTitle:
              "allow_teacher_to_modify_their_messages_subtext",
            depth: 3,
            conf_id: 5015,
            configName: CONFIGURATION.CAN_STAFF_MODIFY_THEIR_MESSAGE,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 3,
          configuration: {
            id: "5015",
          },
        },
      ],
    },
    studentSetting: {
      labelKey: "student_settings",
      showGridLayout: false,
      metadata: {
        depth: 2,
      },
      nodes: [
        {
          labelKey: "allow_student_to_initiate_conversations_with_teachers",
          metadata: {
            configurationSubTitle:
              "allow_student_to_initiate_conversations_with_teachers_subtext",

            depth: 3,
            conf_id: 5016,
            configName: CONFIGURATION.CAN_STUDENT_INITIATE_CHAT,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 1,

          configuration: {
            id: "5016",
          },
        },
        {
          labelKey: "allow_students_to_modify_their_messages",
          metadata: {
            configurationSubTitle:
              "allow_students_to_modify_their_messages_subtext",

            depth: 3,
            conf_id: 5017,
            configName: CONFIGURATION.CAN_STUDENT_MODIFY_THEIR_MESSAGE,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 2,

          configuration: {
            id: "5017",
          },
        },
      ],
    },
    familySetting: {
      labelKey: "family_settings_title",
      showGridLayout: false,
      metadata: {
        depth: 2,
      },
      nodes: [
        {
          labelKey:
            "allow_family_members_to_initiate_conversations_with_teachers",
          metadata: {
            configurationSubTitle:
              "allow_family_members_to_initiate_conversations_with_teachers_subtext",

            depth: 3,
            conf_id: 5018,
            configName: CONFIGURATION.CAN_FAMILY_INITIATE_CHAT,
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 1,

          configuration: {
            id: "5018",
          },
        },
        {
          labelKey: "allow_family_members_to_modify_their_messages",
          metadata: {
            depth: 3,
            conf_id: 22817969625109422n,
            configName: CONFIGURATION.CAN_FAMILY_MODIFY_THEIR_MESSAGE,
            configurationSubTitle:
              "allow_family_members_to_modify_their_messages_subtext",
          },
          uiType: "BOOLEAN",
          viewType: "CONFIGURATION",
          displaySequence: 2,
          configuration: {
            id: "22817969625109422",
          },
        },
      ],
    },
  };
};

export const getParsedConfigData = ({ subModule, configurationsMap }) => {
  const parsedSubModuleNodes = _.map(subModule.nodes, node => {
    const subNode = _.get(node, "nodes", []);
    const nodeValue = _.isEmpty(subNode)
      ? node
      : getParsedConfigData({ subModule: node, configurationsMap });
    const configurationId = _.get(node, "configuration.id");
    const matchedConfiguration = _.get(configurationsMap, configurationId, {});

    const mergedConfigurationObj = {
      ...nodeValue,
      ...matchedConfiguration,
    };

    return mergedConfigurationObj;
  });

  return {
    ...subModule,
    nodes: parsedSubModuleNodes,
  };
};
